import {
  BPJS_CEILING,
  BPJS_HEALTH_EMPLOYER_PERCENTAGE,
  CONTRIBUTION_CONFIGS,
  getContributionMaxAmount,
  getJKKRate,
} from "../Constants/bpjs";
import {
  EMPLOYER_CONTRIBUTION_ALLOWANCE_TYPES,
  VARIABLE_ALLOWANCE_TYPES,
} from "../Constants/business";
import { monthBounds, toCalendarDate } from "./calendarDate";

// A statutory contribution is derived from the wage and the rate in force, not
// negotiated. The amount saved on an employee is only a snapshot of the rates
// on the day the type was picked, so it goes stale the moment a ceiling moves
// or the base salary changes: the JP employee cap went from Rp105,474 to
// Rp110,863 and every employee saved before that still carries the old figure.
//
// `date` is the period being priced, not the day the screen is open. A payroll
// row for January 2026 is owed January's ceiling even when it is rendered in
// September. `riskLevel` is the employee's, and only moves JKK. Returns null
// for anything that is not a statutory contribution, which is the signal to use
// the amount as entered.
export const getStatutoryContributionAmount = (
  type,
  baseSalary,
  date,
  riskLevel,
) => {
  const config = CONTRIBUTION_CONFIGS[type?.value || type];
  if (!config) return null;

  const wage = Number(baseSalary) || 0;
  const calculationBase = config.usesCeiling
    ? Math.min(wage, BPJS_CEILING)
    : wage;
  const percentage = config.usesRiskLevel
    ? getJKKRate(riskLevel)
    : config.percentage;

  const amount = Math.round(calculationBase * percentage);
  const maxAmount = getContributionMaxAmount(config, date);

  return maxAmount ? Math.min(amount, maxAmount) : amount;
};

// BPJS is levied on upah - base salary plus the tunjangan tetap - not on the
// base salary alone. The calculator asks for that split directly, with one
// field for tunjangan tetap and one for tunjangan tidak tetap; here the same
// split is already recorded as the frequency, so a monthly allowance is the
// fixed one and anything annual or one-off is not - except the types that
// depend on attendance or performance, which are not tetap however often they
// are paid. The employer's own BPJS contributions ride along as allowances and
// are not wage, so they stay out too.
export const getContributionWage = (employee) => {
  const baseSalary = Number(employee?.baseSalary) || 0;
  const fixedAllowances = (employee?.allowances?.items || []).filter((item) => {
    const type = item?.type?.value || item?.type;
    return (
      item?.frequency === "MONTHLY" &&
      !isEmployerContributionAllowance(item) &&
      !VARIABLE_ALLOWANCE_TYPES.includes(type)
    );
  });

  return fixedAllowances.reduce(
    (wage, item) => wage + (Number(item?.amount) || 0),
    baseSalary,
  );
};

// The employer's JKK/JKM premium, when it is stored as an allowance.
export const isEmployerContributionAllowance = (item) =>
  EMPLOYER_CONTRIBUTION_ALLOWANCE_TYPES.includes(item?.type?.value || item?.type);

const BPJS_KETENAGAKERJAAN_DEDUCTION_TYPES = [
  "BPJS_KETENAGAKERJAAN_JHT",
  "BPJS_KETENAGAKERJAAN_JP",
];

// The BPJS premiums the employer pays on the employee's behalf. They are
// penghasilan bruto: example I.1 of PMK 168/2023 carries Rp80,000 of JKK and
// JKM a month in the gross column. Like the deductions, they are derived from
// upah every month rather than read off a stored allowance, which is only a
// snapshot of the wage on the day it was saved - and until now had to be added
// by hand and marked taxable before it reached bruto at all. No ceiling on JKK
// or JKM; BPJS Kesehatan stops at Rp12,000,000 of upah.
//
// An employee enrolled in BPJS Ketenagakerjaan - a JHT or JP deduction, or a
// JKK/JKM allowance on file - is owed both premiums. Same rule as mobile
// (pajakind/mobile-user#439).
export const getEmployerPremiums = (employee) => {
  const typeOf = (item) => item?.type?.value || item?.type;
  const deductions = employee?.deductions?.items || [];
  const allowances = employee?.allowances?.items || [];
  const wage = getContributionWage(employee);

  const hasHealth = deductions.some(
    (item) => typeOf(item) === "BPJS_KESEHATAN",
  );
  const hasEmployment =
    deductions.some((item) =>
      BPJS_KETENAGAKERJAAN_DEDUCTION_TYPES.includes(typeOf(item)),
    ) || allowances.some(isEmployerContributionAllowance);

  const health = hasHealth
    ? Math.min(wage, BPJS_CEILING) * BPJS_HEALTH_EMPLOYER_PERCENTAGE
    : 0;
  const jkk = hasEmployment
    ? getStatutoryContributionAmount(
        "BPJS_KETENAGAKERJAAN_JKK",
        wage,
        null,
        employee?.riskLevel,
      )
    : 0;
  const jkm = hasEmployment
    ? getStatutoryContributionAmount("BPJS_KETENAGAKERJAAN_JKM", wage)
    : 0;

  return { health, jkk, jkm, total: health + jkk + jkm };
};

// What a stored deduction is worth in the period being priced. Deductions
// only: an allowance may carry a contribution type to describe the employer's
// share, which is a different percentage than the employee's.
export const getDeductionAmount = (item, baseSalary, date) => {
  const statutory = getStatutoryContributionAmount(
    item?.type,
    baseSalary,
    date,
  );
  return statutory === null ? Number(item?.amount) || 0 : statutory;
};

const mapBenefitItem = (item, type) => ({
  name: item.name,
  description: item.description,
  type: item.type?.value || item.type,
  amount: Number(item.amount),
  frequency: item.frequency?.value || item.frequency,
  startDate: item.startDate,
  endDate: item.endDate || null,
  ...(type === "allowance" && { isTaxable: item.isTaxable }),
  ...(type === "deduction" && { reduceIncome: item.reduceIncome }),
});

//Checks if a benefit item (allowance/deduction) is empty
export const isEmptyBenefitItem = (item) =>
  !item.name?.trim() &&
  !item.type &&
  (!item.amount || item.amount <= 0) &&
  !item.frequency?.value &&
  !item.startDate;

// Accepts either { year, month } or a bare month value. A bare month keeps the
// old call sites working and assumes the current year.
export const normalizePeriod = (period) => {
  if (period == null || period === "") return null;

  if (typeof period === "object") {
    const month = parseInt(period.month, 10);
    if (!month) return null;
    const year = parseInt(period.year, 10) || new Date().getFullYear();
    return { year, month };
  }

  const month = parseInt(period, 10);
  if (!month) return null;
  return { year: new Date().getFullYear(), month };
};

// Filter benefits by the payroll period being run — never by "today".
// A historical or future payroll run has to see exactly the benefits that were
// in force during that month.
export const filterBenefitsByMonth = (benefits, period) => {
  const normalized = normalizePeriod(period);
  if (!normalized || !benefits) return benefits;

  const { year, month } = normalized;
  // Calendar days as "YYYY-MM-DD", so the comparison is the same wherever the
  // page is open and matches the payroll Lambda to the day.
  const bounds = monthBounds(year, month);

  return benefits.filter((benefit) => {
    const startDate = toCalendarDate(benefit.startDate);
    const endDate = toCalendarDate(benefit.endDate);
    const frequency = benefit.frequency?.value || benefit.frequency;
    const startMonth = startDate ? Number(startDate.slice(5, 7)) : null;
    const startYear = startDate ? Number(startDate.slice(0, 4)) : null;

    // Does the benefit's own interval overlap the payroll period at all?
    const overlapsPeriod =
      (!startDate || startDate <= bounds.end) &&
      (!endDate || endDate >= bounds.start);

    switch (frequency) {
      case "MONTHLY":
        return overlapsPeriod;

      case "ANNUAL":
        // Paid once a year, in its anniversary month.
        if (!startDate) return false;
        return overlapsPeriod && startMonth === month;

      case "ONE_TIME":
        // Paid exactly once, in the month it starts.
        if (!startDate) return false;
        return startMonth === month && startYear === year;

      default:
        return true;
    }
  });
};

// Prepare benefits data for display
export const prepareBenefitsData = (
  items,
  type = "allowance",
  period = null,
  existingBenefits = null,
) => {
  const hasEmptyItems = items.length > 0 && isEmptyBenefitItem(items[0]);

  if (hasEmptyItems) {
    return existingBenefits ? existingBenefits : [];
  }

  const preparedItems = items.map((item) => mapBenefitItem(item, type));

  if (!existingBenefits || !period) {
    return preparedItems;
  }

  const remainingExistingItems = existingBenefits
    .filter((item) => {
      const itemForFiltering = {
        ...item,
        startDate: item.startDate,
        endDate: item.endDate,
        frequency: { value: item.frequency },
      };

      const filteredResult = filterBenefitsByMonth([itemForFiltering], period);

      return filteredResult.length === 0;
    })
    .map((item) => mapBenefitItem(item, type));

  return [...remainingExistingItems, ...preparedItems];
};

// Validate benefit item
export const validateBenefitItem = (item, type = "allowance") => {
  const errors = {};
  const errorPrefix =
    type === "allowance"
      ? "employees.allowances.error"
      : "employees.deductions.error";

  if (!item.name?.trim()) errors.name = `${errorPrefix}.name`;
  if (!item.type?.value) errors.type = `${errorPrefix}.type`;
  if (!item.amount || item.amount <= 0) errors.amount = `${errorPrefix}.amount`;
  if (!item.frequency?.value) errors.frequency = `${errorPrefix}.frequency`;
  if (!item.startDate) errors.startDate = `${errorPrefix}.startDate`;

  if (item.frequency?.value !== "ONE_TIME" && item.endDate) {
    const startDate = new Date(item.startDate);
    const endDate = new Date(item.endDate);

    if (endDate <= startDate) {
      errors.endDate = `${errorPrefix}.endDate`;
    }
  }

  if (
    type === "deduction" &&
    item.type?.value === "LIFE_INSURANCE_PREMIUM" &&
    item.amount > 3000000
  ) {
    errors.amount = "employees.deductions.error.life_insurance_max";
  }

  return Object.keys(errors).length > 0 ? errors : null;
};

// Get empty form errors for allowances or deductions
export const getEmptyFormErrors = (type = "allowance") => {
  const errorPrefix =
    type === "allowance"
      ? "employees.allowances.error"
      : "employees.deductions.error";
  const errorKey = type === "allowance" ? "allowanceItems" : "deductionItems";

  return {
    [errorKey]: [
      {
        name: `${errorPrefix}.name`,
        type: `${errorPrefix}.type`,
        amount: `${errorPrefix}.amount`,
        frequency: `${errorPrefix}.frequency`,
        startDate: `${errorPrefix}.startDate`,
        endDate: `${errorPrefix}.endDate`,
      },
    ],
  };
};

// Get the period label for display
export const getMonthName = (period) => {
  const normalized = normalizePeriod(period);
  if (!normalized) return "";
  return `${normalized.month}/${normalized.year}`;
};
